/**
 * me/quiz/$activationId/result.tsx — Student result page (Phase H.2).
 *
 * Two-step fetch:
 *   1. GET /api/quiz/my-attempts?activationId=X — find the
 *      student's attempt for this activation (most recent
 *      first; usually exactly 1).
 *   2. GET /api/quiz/attempt/:id — full breakdown (per-question
 *      correct/wrong + the correct answer + the student's
 *      selected answer).
 *
 * Renders a per-question breakdown table + a top-line summary
 * card. The same quiz:ended WS event the player listens for
 * also lands the user here automatically, so finishing a quiz
 * navigates straight to this page.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { createFileRoute, redirect, useParams } from '@tanstack/react-router';
import { PageHeader } from '../../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../../components/ui/StatePanel.js';
import { BackToSessionLink } from '../../../../components/quiz/BackToSessionLink.js';
import { freshSessionQuery } from '../../../../lib/api/queries.js';

export const Route = createFileRoute('/me/quiz/$activationId/result')({
  // Forwarded from the take page (QuizPlayer on quiz:ended) so the
  // "Kembali ke sesi" link survives the automatic navigation here.
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
  }),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role === 'parent') {
      throw redirect({ to: '/parent' });
    }
    if (user.role === 'admin' || user.role === 'tutor') {
      throw redirect({ to: user.role === 'admin' ? '/admin' : '/dashboard' });
    }
    return null;
  },
  component: StudentResultPage,
});

interface AttemptSummary {
  id: string;
  totalCorrect: number;
  totalQuestions: number;
  starsEarned: number;
  percentage: number;
  status: string;
  joinedAt: string;
  completedAt: string | null;
}

interface AttemptDetail {
  attempt: {
    id: string;
    activationId: string;
    totalQuestions: number;
    totalCorrect: number;
    starsEarned: number;
    percentage: number;
    status: string;
  };
  quiz: { id: string; title: string; description: string | null } | null;
  answers: Array<{
    questionId: string;
    prompt: string;
    type: string;
    options: string[] | null;
    correctAnswer: string;
    selected: string;
    isCorrect: boolean;
    starEarned: number;
    points: number;
  }>;
}

function StudentResultPage(): ReactNode {
  const { activationId } = useParams({ strict: false }) as { activationId: string };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AttemptSummary | null>(null);
  const [detail, setDetail] = useState<AttemptDetail | null>(null);
  const [quizTitle, setQuizTitle] = useState<string>('');
  const { from: fromParam } = Route.useSearch();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const listR = await fetch(
          `/api/quiz/my-attempts?activationId=${encodeURIComponent(activationId)}`,
          { credentials: 'include' },
        );
        const listJ = (await listR.json()) as { ok: boolean; data?: AttemptSummary[]; error?: { message: string } };
        if (cancelled) return;
        if (!listJ.ok || !listJ.data || listJ.data.length === 0) {
          setLoadError('Belum ada attempt untuk quiz ini. Mungkin kamu belum join.');
          setLoading(false);
          return;
        }
        const a = listJ.data[0]!;
        setSummary(a);
        const detR = await fetch(`/api/quiz/attempt/${encodeURIComponent(a.id)}`, { credentials: 'include' });
        const detJ = (await detR.json()) as { ok: boolean; data?: AttemptDetail; error?: { message: string } };
        if (cancelled) return;
        if (!detJ.ok || !detJ.data) {
          setLoadError(detJ.error?.message ?? 'Gagal memuat detail');
          setLoading(false);
          return;
        }
        setDetail(detJ.data);
        setQuizTitle(detJ.data.quiz?.title ?? 'Quiz');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activationId]);

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Hasil Quiz" title="Memuat…" />
      </main>
    );
  }
  if (loadError || !summary) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Hasil Quiz" title="Tidak ada hasil" />
        <StatePanel variant="error" title="Gagal memuat" description={loadError ?? 'Tidak ditemukan'} />
      </main>
    );
  }

  const correct = detail?.answers.filter((a) => a.isCorrect).length ?? summary.totalCorrect;
  const stars = detail?.answers.reduce((s, a) => s + a.starEarned, 0) ?? summary.starsEarned;
  const percentage = summary.percentage;

  return (
    <main className="page-shell" data-testid="student-result">
      <PageHeader
        eyebrow="Hasil Quiz"
        title={quizTitle}
        description={`${correct} / ${summary.totalQuestions} benar · ${stars}⭐ · ${percentage}%`}
        action={
          <BackToSessionLink
            from={fromParam}
            fallbackHref="/me"
            fallbackLabel="← Kembali ke Dasbor"
          />
        }
      />

      <section className="section">
        <h2 className="section-title">Ringkasan</h2>
        <ul className="metric-row">
          <li className="card">
            <p className="section-eyebrow">Benar</p>
            <p className="metric-value">{correct} / {summary.totalQuestions}</p>
          </li>
          <li className="card">
            <p className="section-eyebrow">Bintang</p>
            <p className="metric-value">{stars}⭐</p>
          </li>
          <li className="card">
            <p className="section-eyebrow">Persentase</p>
            <p className="metric-value">{percentage}%</p>
          </li>
        </ul>
      </section>

      <section className="section">
        <h2 className="section-title">Per soal</h2>
        {!detail || detail.answers.length === 0 ? (
          <StatePanel variant="empty" title="Belum ada soal dijawab" />
        ) : (
          <ol className="preview-question-list" data-testid="result-questions">
            {detail.answers.map((a, i) => {
              const options = a.options;
              const userIdx = a.selected;
              const correctIdx = a.correctAnswer;
              return (
                <li key={a.questionId} className="card question-editor">
                  <header className="question-editor-header">
                    <h4 className="question-editor-title">
                      Soal {i + 1} · {a.isCorrect ? '✅ Benar' : '❌ Kurang tepat'}
                    </h4>
                    <span className="card-meta">
                      {a.starEarned} / {a.points}⭐
                    </span>
                  </header>
                  <p className="preview-prompt">{a.prompt}</p>
                  {a.type === 'true_false' ? (
                    <ul className="preview-options">
                      {(['true', 'false'] as const).map((v) => {
                        const isUser = userIdx === v;
                        const isCorrect = correctIdx === v;
                        return (
                          <li
                            key={v}
                            className={`preview-option ${isCorrect ? 'is-correct' : ''} ${isUser && !isCorrect ? 'is-wrong' : ''}`}
                          >
                            {v === 'true' ? 'Benar' : 'Salah'}
                            {isCorrect ? ' ✓ jawaban benar' : ''}
                            {isUser ? ' (pilihan kamu)' : ''}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <ul className="preview-options">
                      {options?.map((opt, j) => {
                        const idStr = String(j);
                        const isUser = userIdx === idStr || (a.type === 'multi_select' && userIdx.split(',').includes(idStr));
                        const isCorrect = correctIdx === idStr || (a.type === 'multi_select' && correctIdx.split(',').includes(idStr));
                        return (
                          <li
                            key={j}
                            className={`preview-option ${isCorrect ? 'is-correct' : ''} ${isUser && !isCorrect ? 'is-wrong' : ''}`}
                          >
                            <span className="preview-option-marker">{String.fromCharCode(65 + j)}.</span>
                            <span>{opt}</span>
                            {isCorrect ? <span className="form-help"> ✓ benar</span> : null}
                            {isUser ? <span className="form-help"> (kamu)</span> : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </main>
  );
}
